import * as React from "react"
import { Fragment } from "react"
import { array, arrayOf, bool, func, number, object, oneOfType, shape, string } from "prop-types"
import { v4 as uuidv4, validate as uuidValidate } from "uuid"
import { HelperShelfMessage, HelperShelfTrigger } from "components/helper_shelf"
import { Box, Button, StackView, Text } from "@planningcenter/tapestry-react"
import Icon from "components/planning_center_icon"
import { Select } from "components/ui"

const Labels = ({
  appEventLabels,
  checkInsLabels,
  isEditing,
  onAppEventChange,
  signupLabelOptions
}) => {
  const defaultLabel = {
    id: uuidv4(),
    conditionalTypes: ["attendee_types/all"],
    label: checkInsLabels[0],
    labelId: checkInsLabels[0]?.id
  }

  const addAppEventLabel = () =>
    onAppEventChange(appEvent => ({
      ...appEvent,
      appEventLabels: [...appEvent.appEventLabels, defaultLabel]
    }))

  const updateAppEventLabel = (id, changes) =>
    onAppEventChange(appEvent => ({
      ...appEvent,
      appEventLabels: appEvent.appEventLabels.map(l => (l.id === id ? { ...l, ...changes } : l))
    }))

  const removeAppEventLabel = id =>
    onAppEventChange(appEvent => ({
      ...appEvent,
      appEventLabels: setLabelsToRemove(id, appEvent.appEventLabels)
    }))

  const setLabelsToRemove = (id, appEventLabels) =>
    appEventLabels.reduce((acc, label) => {
      if (label.id === id && uuidValidate(label.id)) return acc
      else if (label.id === id) return [...acc, { ...label, _destroy: true }]

      return [...acc, label]
    }, [])

  return (
    <>
      <StackView axis="horizontal" alignment="center" gap={1} marginTop={2}>
        <h2 className="label">Labels</h2>
        <HelperShelfTrigger id="check-ins-labels-desc" />
      </StackView>
      <HelperShelfMessage id="check-ins-labels-desc">
        You can create or customize labels for this signup in{" "}
        <a href="https://check-ins.planningcenteronline.com/labels" rel="noreferror">
          Check-Ins
        </a>
        .
      </HelperShelfMessage>
      {isEditing ? (
        <EditLabels
          addAppEventLabel={addAppEventLabel}
          appEventLabels={appEventLabels}
          signupLabelOptions={signupLabelOptions}
          checkInsLabels={checkInsLabels}
          removeAppEventLabel={removeAppEventLabel}
          updateAppEventLabel={updateAppEventLabel}
        />
      ) : (
        <ShowLabels appEventLabels={appEventLabels} signupLabelOptions={signupLabelOptions} />
      )}
    </>
  )
}

Labels.propTypes = {
  appEventLabels: arrayOf(object).isRequired,
  checkInsLabels: arrayOf(object).isRequired,
  isEditing: bool,
  onAppEventChange: func.isRequired,
  signupLabelOptions: array
}

const ShowLabels = ({ appEventLabels, signupLabelOptions }) => {
  const labelsToDisplay = appEventLabels
    .map(appEventLabel => {
      const category = signupLabelOptions
        .flatMap(t => t.options)
        .find(c => c.id === appEventLabel.conditionalTypes[0])?.name

      if (category) {
        return {
          id: appEventLabel.id,
          name: appEventLabel.label.name,
          category
        }
      }
    })
    .filter(label => label)

  return (
    <>
      {labelsToDisplay.length ? (
        <StackView spacing={1}>
          {labelsToDisplay.map(label => (
            <Label key={`show-labels-${label.id}`} label={label} />
          ))}
        </StackView>
      ) : (
        <NotPrintingLabels />
      )}
    </>
  )
}

ShowLabels.propTypes = {
  appEventLabels: arrayOf(object).isRequired,
  signupLabelOptions: array.isRequired
}

const EditLabels = ({
  addAppEventLabel,
  appEventLabels,
  checkInsLabels,
  removeAppEventLabel,
  signupLabelOptions,
  updateAppEventLabel
}) => {
  const labelOptions = checkInsLabels.map(label => ({ label: label.name, value: label.id }))

  return (
    <StackView spacing={1} alignment="start">
      <StackView spacing={1} alignSelf="stretch">
        {appEventLabels.map(appEventLabel => (
          <EditingLabel
            key={`edit-labels-${appEventLabel.id}`}
            appEventLabel={appEventLabel}
            labelOptions={labelOptions}
            removeAppEventLabel={removeAppEventLabel}
            signupLabelOptions={signupLabelOptions}
            updateAppEventLabel={updateAppEventLabel}
          />
        ))}
      </StackView>
      <Button title="Add label" variant="outline" size="sm" onClick={addAppEventLabel} />
    </StackView>
  )
}

EditLabels.propTypes = {
  addAppEventLabel: func,
  appEventLabels: arrayOf(object).isRequired,
  checkInsLabels: arrayOf(object).isRequired,
  removeAppEventLabel: func,
  signupLabelOptions: array.isRequired,
  updateAppEventLabel: func
}

const EditingLabel = ({
  appEventLabel,
  labelOptions,
  removeAppEventLabel,
  signupLabelOptions,
  updateAppEventLabel
}) => {
  const selectedCategory = signupLabelOptions
    .flatMap(t => t.options)
    .find(c => c.id === appEventLabel.conditionalTypes[0])

  if (!selectedCategory) return null

  return (
    <StackView axis="horizontal" alignment="center" spacing={2}>
      <StackView axis="horizontal" spacing={1} flex={1}>
        <select
          name="label_selections"
          className="select"
          id="label_selections"
          value={selectedCategory.id}
          onChange={e =>
            updateAppEventLabel(appEventLabel.id, { conditionalTypes: [e.target.value] })
          }
        >
          {signupLabelOptions.map(category =>
            category?.name ? (
              <Fragment key={`category-option-${category.name}`}>
                <optgroup label={category.name}>
                  {category.options.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </optgroup>
              </Fragment>
            ) : (
              <Fragment key={`category-option-${category.name}`}>
                {category.options.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Fragment>
            )
          )}
        </select>
        <Select
          name="label_name"
          className="select"
          id="label_name"
          options={labelOptions}
          value={appEventLabel.labelId}
          onChange={e => updateAppEventLabel(appEventLabel.id, { labelId: e.target.value })}
        />
      </StackView>
      <Button
        onClick={() => removeAppEventLabel(appEventLabel.id)}
        variant="naked"
        theme="destroy"
        aria-label="remove label"
      >
        <Icon symbol="general#x" />
      </Button>
    </StackView>
  )
}

EditingLabel.propTypes = {
  appEventLabel: shape({
    conditionalTypes: array.isRequired,
    labelId: oneOfType([string, number]).isRequired
  }),
  labelOptions: arrayOf(object).isRequired,
  removeAppEventLabel: func,
  signupLabelOptions: array.isRequired,
  updateAppEventLabel: func
}

const Label = ({ label }) => {
  return (
    <Box className="drawer" padding={1}>
      {label.name}
      <Text color="var(--t-fill-color-neutral-010)" size={4}>
        {label.category}
      </Text>
    </Box>
  )
}

Label.propTypes = {
  label: shape({
    name: string.isRequired,
    category: string.isRequired
  }).isRequired
}

const NotPrintingLabels = () => {
  return (
    <StackView
      axis="horizontal"
      alignment="center"
      marginTop={1}
      spacing={1}
      color="var(--t-fill-color-neutral-020)"
    >
      <Icon symbol="general#x" css={{ fontSize: 12 }} />
      Not printing labels
    </StackView>
  )
}

export default Labels
